import { useEffect, useState } from "react";
import { Download, Users, MousePointerClick, ShoppingBag, AlertTriangle, CalendarX, Phone } from "lucide-react";
import { smsApi, reais, pct, baixarCsv, dataBr, agendadaNoFuturo } from "./api";
import Dialogo from "./Dialogo";
import RetornoCampanha from "./RetornoCampanha";
import VendasPorTelefone from "./VendasPorTelefone";
import { RelatorioCarregando } from "../Carregando";

/**
 * Relatorio da campanha: funil, ROI e comparacao com o grupo de controle.
 *
 * ROI vem da receita atribuida pelo LINK — exata. O lift vem da compra na
 * janela, medida igual nos dois grupos, e so' aparece quando a amostra do
 * controle e' grande o bastante pra significar alguma coisa. A manchete, nos
 * dois canais, e' o cruzamento por telefone + link (RetornoCampanha e
 * VendasPorTelefone); a lista pelo link continua no card "Pedidos pelo link".
 */
export default function RelatorioCampanhaSMS({ campanhaId, onVoltar, onCancelada }) {
  const [r, setR] = useState(null);
  const [erro, setErro] = useState(null);
  const [mostrarPedidos, setMostrarPedidos] = useState(false);
  const [confirmCancelar, setConfirmCancelar] = useState(false);
  const [cancelando, setCancelando] = useState(false);

  useEffect(() => {
    smsApi.relatorio(campanhaId).then(setR)
      .catch(() => setErro("Não foi possível carregar o relatório desta campanha."));
  }, [campanhaId]);

  const exportar = async () => {
    const linhas = await smsApi.destinatarios(campanhaId);
    baixarCsv(`campanha-${campanhaId.slice(0, 8)}`, linhas);
  };

  // "← Voltar" aparece já no carregamento e no erro (antes só com o relatório aberto).
  const voltar = <button onClick={onVoltar} className="text-sm text-primary hover:underline mb-1">← Voltar</button>;
  if (erro) return <div className="space-y-2">{voltar}<p className="text-destructive">{erro}</p></div>;
  if (!r) return <div className="space-y-2">{voltar}<RelatorioCarregando /></div>;

  const { funil, financeiro, origem_das_vendas: origem, grupo_controle: gc, cruzamento: cz } = r;

  const ehVoz = r.campanha.canal === "voz";
  const passos = [
    { icone: ehVoz ? Phone : Users, rotulo: ehVoz ? "Ligações" : "Enviados", valor: funil.enviados, extra: null },
    // "Discadas" na voz: a operadora quase nunca diz se atendeu (mesmo nome do painel).
    { icone: Users, rotulo: ehVoz ? "Discadas" : "Entregues", valor: funil.entregues, extra: pct(funil.taxa_entrega_decimos) },
    ehVoz
      ? { icone: Phone, rotulo: "Atendidas", valor: funil.atendidas, extra: pct(funil.taxa_atendimento_decimos) }
      : { icone: MousePointerClick, rotulo: "Cliques no link", valor: funil.cliques, extra: pct(funil.taxa_clique_decimos) },
    // Clicavel no SMS: abre a lista dos pedidos que vieram do link. Na voz a
    // lista (por telefone) mora no card "Quem recebeu e pediu".
    ehVoz
      ? { icone: ShoppingBag, rotulo: "Pedidos de quem recebeu", valor: cz?.pedidos ?? 0, extra: null }
      : { icone: ShoppingBag, rotulo: "Pedidos pelo link", valor: funil.pedidos, extra: null,
          acao: () => setMostrarPedidos((v) => !v) },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <button onClick={onVoltar} className="text-sm text-primary hover:underline mb-1">← Voltar</button>
          <h2 className="text-xl font-bold">{r.campanha.nome}</h2>
          <p className="text-sm text-muted-foreground">
            {agendadaNoFuturo(r.campanha.agendada_para)
              ? <span className="text-amber-500 font-medium">Agendada para {dataBr(r.campanha.agendada_para)}</span>
              : dataBr(r.campanha.enviada_em)} · {r.campanha.link}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {r.campanha.status === "agendada" && (
            <button onClick={() => setConfirmCancelar(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-red-500/40 text-red-500 px-3 py-2 text-sm font-medium hover:bg-red-500/10 transition-colors">
              <CalendarX className="w-4 h-4" /> Cancelar agendamento
            </button>
          )}
          <button onClick={exportar}
            className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium">
            <Download className="w-4 h-4" /> Exportar lista
          </button>
        </div>
      </div>

      <RetornoCampanha financeiro={financeiro} funil={funil} cz={cz} ehVoz={ehVoz} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {passos.map(({ icone: Icone, rotulo, valor, extra, acao }) => (
          <div key={rotulo} onClick={acao}
            className={`rounded-xl border bg-card p-4 ${acao ? "cursor-pointer hover:border-primary transition-colors" : ""}`}>
            <Icone className="w-4 h-4 text-muted-foreground mb-2" />
            <p className="text-2xl font-bold tabular-nums">{(valor || 0).toLocaleString("pt-BR")}</p>
            <p className="text-xs text-muted-foreground">{rotulo}</p>
            {extra && <p className="text-xs text-primary mt-1">{extra}</p>}
            {acao && <p className="text-[10px] text-primary mt-1">{mostrarPedidos ? "ocultar lista" : "ver a lista"}</p>}
          </div>
        ))}
      </div>

      {mostrarPedidos && (
        <div className="rounded-xl border bg-card p-5">
          <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-muted-foreground" /> Pedidos desta campanha
          </h3>
          {!r.pedidos_lista?.length ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Nenhum pedido pelo link ainda.
            </p>
          ) : (
            <div className="divide-y -my-1">
              {r.pedidos_lista.map((p) => (
                <div key={p.codigo} className="py-2.5 flex items-center gap-3 text-sm">
                  <span className="font-semibold tabular-nums shrink-0">{p.codigo}</span>
                  <span className="flex-1 truncate text-muted-foreground">{p.cliente_nome || "—"}</span>
                  <span className="text-xs text-muted-foreground tabular-nums shrink-0">{dataBr(p.criado_em)}</span>
                  <span className="font-semibold tabular-nums shrink-0">{reais(p.valor_centavos)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {funil.falhas > 0 && (
        <p className="text-sm text-muted-foreground">
          {funil.falhas} não chegaram (aparelho desligado, fora de área ou número bloqueado).
          {financeiro.creditos_estornados > 0 &&
            ` ${financeiro.creditos_estornados} envios foram devolvidos ao seu saldo.`}
        </p>
      )}

      <VendasPorTelefone cz={cz} ehVoz={ehVoz} />

      {!ehVoz && <div className="rounded-xl border bg-card p-4">
        <h3 className="font-semibold text-sm mb-3">De onde vieram as vendas pelo link</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Clientes da sua base</span>
            <span className="tabular-nums">{origem.da_base} · {reais(origem.receita_base_centavos)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Clientes novos (link repassado)</span>
            <span className="tabular-nums">{origem.novos} · {reais(origem.receita_novos_centavos)}</span>
          </div>
        </div>
        {origem.novos > 0 && (
          <p className="text-xs text-muted-foreground mt-3">
            Quem recebeu compartilhou o link e trouxe gente nova — essas vendas também são da campanha.
          </p>
        )}
      </div>}

      {gc.n_controle > 0 && (
        <div className="rounded-xl border bg-card p-4">
          <h3 className="font-semibold text-sm mb-3">Comparação com o grupo de controle</h3>
          {!gc.amostra_suficiente ? (
            <p className="text-sm text-amber-600 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              O grupo de controle tem só {gc.n_controle} pessoas — pouco para o resultado significar algo.
              Numa lista desse tamanho, um ou dois pedidos a mais mudam a conta inteira.
            </p>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Receberam o SMS ({gc.n_teste})</span>
                <span className="tabular-nums">{pct(gc.taxa_teste_decimos)} compraram</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Não receberam ({gc.n_controle})</span>
                <span className="tabular-nums">{pct(gc.taxa_controle_decimos)} compraram</span>
              </div>
              <div className="flex justify-between border-t pt-2 font-semibold">
                <span>Diferença que o SMS fez</span>
                <span className="tabular-nums text-primary">+{pct(gc.lift_decimos)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {confirmCancelar && (
        <Dialogo icone={CalendarX} tom="erro" titulo="Cancelar este agendamento?"
          onFechar={() => setConfirmCancelar(false)}
          acoes={[
            { rotulo: "Manter agendada", primario: true, onClick: () => setConfirmCancelar(false) },
            { rotulo: cancelando ? "Cancelando..." : "Cancelar e devolver créditos",
              desabilitado: cancelando,
              onClick: async () => {
                setCancelando(true);
                try {
                  const res = await smsApi.cancelar(campanhaId);
                  onCancelada?.(res.cancelados);
                } catch (e) {
                  window.alert(e?.response?.data?.detail || "Não foi possível cancelar.");
                  setConfirmCancelar(false);
                } finally {
                  setCancelando(false);
                }
              } },
          ]}>
          <p className="text-center">
            Nenhuma mensagem será enviada e os{" "}
            <strong className="text-foreground tabular-nums">{funil.enviados}</strong>{" "}
            créditos reservados voltam para a carteira.
          </p>
        </Dialogo>
      )}

      <div className="rounded-xl border bg-card p-4">
        <h3 className="font-semibold text-sm mb-2">
          {ehVoz ? "Roteiro da ligação" : "Mensagem enviada"}
        </h3>
        <p className="text-sm bg-muted/40 rounded-lg p-3 whitespace-pre-wrap">{r.campanha.mensagem}</p>
        {ehVoz && r.campanha.audio_url && (
          <audio controls src={r.campanha.audio_url} className="w-full mt-3" />
        )}
      </div>
    </div>
  );
}
